import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import {
  getAffiliateDashboard,
  updatePayoutDetails,
  setAffiliateCode,
  createAffiliateCode,
  getAffiliateCodeDetail,
  updateAffiliateCode,
} from '../../services/api';

function AffiliatePage() {
  const navigate = useNavigate();
  const { hasAffiliateProfile, hasSubscription, refreshProfile, loading: userLoading } = useUser();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  // Create code form
  const [newCode, setNewCode] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState('');
  const [createMsgType, setCreateMsgType] = useState('');

  // Payout form
  const [payoutMethod, setPayoutMethod] = useState('');
  const [payoutHandle, setPayoutHandle] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [savingPayout, setSavingPayout] = useState(false);
  const [payoutMessage, setPayoutMessage] = useState('');

  // Rename default code
  const [renameCode, setRenameCode] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renameMsg, setRenameMsg] = useState('');

  // Expanded code detail
  const [expandedCode, setExpandedCode] = useState(null);
  const [codeDetail, setCodeDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit label
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  useEffect(() => {
    if (!userLoading) {
      fetchDashboard();
    }
  }, [userLoading, hasAffiliateProfile]);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const data = await getAffiliateDashboard();
      setDashboard(data);
      setPayoutMethod(data.payout_method || '');
      setPayoutHandle(data.payout_handle || '');
      setPayoutNotes(data.payout_notes || '');
    } catch (err) {
      // Not a paying user or no profile
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async () => {
    if (!newLabel.trim()) return;
    setCreating(true);
    setCreateMsg('');
    try {
      await createAffiliateCode(newCode.trim().toLowerCase() || '', newLabel.trim());
      setCreateMsg('Code created successfully.');
      setCreateMsgType('success');
      setNewCode('');
      setNewLabel('');
      await fetchDashboard();
    } catch (err) {
      setCreateMsg(err.response?.data?.error || 'Failed to create code.');
      setCreateMsgType('error');
    } finally {
      setCreating(false);
    }
  };

  const handleSavePayout = async () => {
    setSavingPayout(true);
    setPayoutMessage('');
    try {
      await updatePayoutDetails({
        payout_method: payoutMethod || null,
        payout_handle: payoutHandle || null,
        payout_notes: payoutNotes || '',
      });
      setPayoutMessage('Saved.');
      setTimeout(() => setPayoutMessage(''), 3000);
    } catch (err) {
      setPayoutMessage('Failed to save.');
    } finally {
      setSavingPayout(false);
    }
  };

  const handleRenameDefault = async () => {
    if (!renameCode.trim()) return;
    setRenaming(true);
    setRenameMsg('');
    try {
      await setAffiliateCode(renameCode.trim().toLowerCase());
      setRenameMsg('Code set permanently.');
      setRenameCode('');
      await fetchDashboard();
      await refreshProfile();
    } catch (err) {
      setRenameMsg(err.response?.data?.error || 'Failed to set code.');
    } finally {
      setRenaming(false);
    }
  };

  const handleExpandCode = async (codeId) => {
    if (expandedCode === codeId) {
      setExpandedCode(null);
      setCodeDetail(null);
      return;
    }
    setExpandedCode(codeId);
    setDetailLoading(true);
    try {
      const detail = await getAffiliateCodeDetail(codeId);
      setCodeDetail(detail);
    } catch (err) {
      setCodeDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleToggleActive = async (codeId, currentActive) => {
    try {
      await updateAffiliateCode(codeId, { is_active: !currentActive });
      await fetchDashboard();
      if (expandedCode === codeId) {
        const detail = await getAffiliateCodeDetail(codeId);
        setCodeDetail(detail);
      }
    } catch (err) {
      console.error('Failed to toggle code active status');
    }
  };

  const handleSaveLabel = async (codeId) => {
    try {
      await updateAffiliateCode(codeId, { campaign_label: editLabelValue });
      setEditingLabel(null);
      await fetchDashboard();
    } catch (err) {
      console.error('Failed to update label');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatCents = (cents) => `$${(cents / 100).toFixed(2)}`;

  if (userLoading || loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <h1 className="text-2xl font-bold text-brand-white mb-8">Affiliate Program</h1>
        <p className="text-muted">Loading...</p>
      </div>
    );
  }

  // Not a paying user
  if (!hasSubscription && !hasAffiliateProfile) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <h1 className="text-2xl font-bold text-brand-white mb-8">Affiliate Program</h1>
        <section className="bg-surface rounded-lg border border-muted p-6">
          <p className="text-muted text-sm mb-3">
            Upgrade to a paid plan to unlock the affiliate program. Share codes with your audience and earn 30% of their subscription in year 1, and 20% year 2+.
          </p>
          <button
            onClick={() => navigate('/pricing')}
            className="px-4 py-2 bg-brand-blue text-white text-sm rounded hover:bg-brand-blue/80 transition-colors"
          >
            View Plans
          </button>
        </section>
      </div>
    );
  }

  if (!dashboard) return null;

  const defaultCode = dashboard.codes?.find(c => c.campaign_label === 'Default') || dashboard.codes?.[dashboard.codes.length - 1];

  return (
    <div className="max-w-4xl mx-auto py-8">
      <h1 className="text-2xl font-bold text-brand-white mb-8">Affiliate Program</h1>

      {/* Earnings Summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        {[
          { label: 'Total Earned', value: formatCents(dashboard.total_earned_cents), color: '#fff' },
          { label: 'Pending Balance', value: formatCents(dashboard.pending_balance_cents), color: '#fbbf24' },
          { label: 'Paid Out', value: formatCents(dashboard.paid_out_cents), color: '#10b981' },
          { label: 'Total Referrals', value: dashboard.total_referral_count, color: '#218aff' },
        ].map((stat, i) => (
          <div key={i} style={{
            padding: '16px',
            backgroundColor: '#1a1a1a',
            border: '1px solid #333',
            borderRadius: '8px',
            textAlign: 'center',
          }}>
            <p style={{ color: '#999', fontSize: '12px', marginBottom: '4px' }}>{stat.label}</p>
            <p style={{ color: stat.color, fontSize: '22px', fontWeight: '700' }}>{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Commission Rates */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: 'rgba(33, 138, 255, 0.05)',
        border: '1px solid rgba(33, 138, 255, 0.2)',
        borderRadius: '8px',
        marginBottom: '24px',
      }}>
        <p style={{ color: '#999', fontSize: '13px', margin: 0 }}>
          <strong style={{ color: '#ccc' }}>Commission rates:</strong>{' '}
          30% of subscription revenue in year 1, 20% year 2+
        </p>
      </div>

      {/* Campaign Codes Section */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Campaign Codes</h2>

        {/* Create New Code */}
        <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#111', borderRadius: '8px', border: '1px solid #333' }}>
          <p style={{ color: '#ccc', fontSize: '14px', marginBottom: '10px', fontWeight: '600' }}>Create New Code</p>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Campaign label (e.g., YouTube Q1 2026)"
              maxLength={100}
              style={{ ...inputStyle, flex: 1, minWidth: '200px' }}
            />
            <input
              type="text"
              value={newCode}
              onChange={(e) => setNewCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="Custom code (optional)"
              maxLength={30}
              style={{ ...inputStyle, width: '180px' }}
            />
            <button
              onClick={handleCreateCode}
              disabled={creating || !newLabel.trim()}
              style={{
                ...btnStyle,
                backgroundColor: creating || !newLabel.trim() ? '#333' : '#218aff',
              }}
            >
              {creating ? 'Creating...' : 'Create Code'}
            </button>
          </div>
          {createMsg && (
            <p style={{ marginTop: '8px', fontSize: '13px', color: createMsgType === 'error' ? '#ef4444' : '#10b981' }}>
              {createMsg}
            </p>
          )}
        </div>

        {/* Codes List */}
        {!dashboard.codes || dashboard.codes.length === 0 ? (
          <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No codes yet. Create one above.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {dashboard.codes.map((c) => (
              <div key={c.id}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  backgroundColor: expandedCode === c.id ? '#1a1a1a' : '#111',
                  border: `1px solid ${expandedCode === c.id ? '#218aff' : '#333'}`,
                  borderRadius: expandedCode === c.id ? '8px 8px 0 0' : '8px',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                  onClick={() => handleExpandCode(c.id)}
                >
                  <code style={{ color: '#218aff', fontWeight: '600', fontSize: '14px', minWidth: '120px' }}>{c.code}</code>
                  {editingLabel === c.id ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flex: 1 }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="text"
                        value={editLabelValue}
                        onChange={(e) => setEditLabelValue(e.target.value)}
                        style={{ ...inputStyle, flex: 1, padding: '4px 8px', fontSize: '12px' }}
                        maxLength={100}
                      />
                      <button onClick={() => handleSaveLabel(c.id)} style={{ ...btnStyle, padding: '4px 10px', fontSize: '11px', backgroundColor: '#218aff' }}>Save</button>
                      <button onClick={() => setEditingLabel(null)} style={{ ...btnStyle, padding: '4px 10px', fontSize: '11px' }}>Cancel</button>
                    </div>
                  ) : (
                    <span
                      style={{ color: '#999', fontSize: '13px', flex: 1, cursor: 'text' }}
                      onClick={(e) => { e.stopPropagation(); setEditingLabel(c.id); setEditLabelValue(c.campaign_label); }}
                      title="Click to edit label"
                    >
                      {c.campaign_label || 'No label'}
                    </span>
                  )}
                  <span style={{ color: '#ccc', fontSize: '12px', minWidth: '80px', textAlign: 'right' }}>
                    {c.referral_count} referral{c.referral_count !== 1 ? 's' : ''}
                  </span>
                  <span style={{ color: '#10b981', fontSize: '13px', fontWeight: '600', minWidth: '80px', textAlign: 'right' }}>
                    {formatCents(c.earned_cents)}
                  </span>
                  <span style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '600',
                    backgroundColor: c.is_active ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    color: c.is_active ? '#10b981' : '#ef4444',
                  }}>
                    {c.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(c.code); }}
                    style={{ ...btnStyle, padding: '4px 10px', fontSize: '11px' }}
                    title="Copy code"
                  >
                    Copy
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyToClipboard(`${window.location.origin}/signup?ref=${c.code}`); }}
                    style={{ ...btnStyle, padding: '4px 10px', fontSize: '11px' }}
                    title="Copy referral link"
                  >
                    Link
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleToggleActive(c.id, c.is_active); }}
                    style={{ ...btnStyle, padding: '4px 10px', fontSize: '11px', color: c.is_active ? '#ef4444' : '#10b981' }}
                  >
                    {c.is_active ? 'Disable' : 'Enable'}
                  </button>
                </div>

                {/* Expanded Code Detail */}
                {expandedCode === c.id && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#0d0d0d',
                    border: '1px solid #218aff',
                    borderTop: 'none',
                    borderRadius: '0 0 8px 8px',
                  }}>
                    {detailLoading ? (
                      <p style={{ color: '#999', fontSize: '13px' }}>Loading...</p>
                    ) : codeDetail ? (
                      <>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '16px' }}>
                          <div style={{ padding: '10px', backgroundColor: '#111', borderRadius: '6px', textAlign: 'center' }}>
                            <p style={{ color: '#999', fontSize: '11px' }}>Earned</p>
                            <p style={{ color: '#fff', fontSize: '16px', fontWeight: '700' }}>{formatCents(codeDetail.earned_cents)}</p>
                          </div>
                          <div style={{ padding: '10px', backgroundColor: '#111', borderRadius: '6px', textAlign: 'center' }}>
                            <p style={{ color: '#999', fontSize: '11px' }}>Pending</p>
                            <p style={{ color: '#fbbf24', fontSize: '16px', fontWeight: '700' }}>{formatCents(codeDetail.pending_cents)}</p>
                          </div>
                          <div style={{ padding: '10px', backgroundColor: '#111', borderRadius: '6px', textAlign: 'center' }}>
                            <p style={{ color: '#999', fontSize: '11px' }}>Paid</p>
                            <p style={{ color: '#10b981', fontSize: '16px', fontWeight: '700' }}>{formatCents(codeDetail.paid_cents)}</p>
                          </div>
                        </div>

                        {codeDetail.commissions && codeDetail.commissions.length > 0 ? (
                          <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #222', borderRadius: '6px' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#111' }}>
                                  <th style={thStyle}>Date</th>
                                  <th style={thStyle}>User</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Payment</th>
                                  <th style={{ ...thStyle, textAlign: 'center' }}>Rate</th>
                                  <th style={{ ...thStyle, textAlign: 'right' }}>Commission</th>
                                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {codeDetail.commissions.map((comm) => (
                                  <tr key={comm.id} style={{ borderBottom: '1px solid #1a1a1a' }}>
                                    <td style={tdStyle}>{new Date(comm.created_at).toLocaleDateString()}</td>
                                    <td style={tdStyle}>{comm.referred_username}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCents(comm.payment_amount_cents)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>{(comm.commission_rate * 100).toFixed(0)}%</td>
                                    <td style={{ ...tdStyle, textAlign: 'right', color: '#10b981', fontWeight: '600' }}>{formatCents(comm.commission_amount_cents)}</td>
                                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                                      <span style={{
                                        padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: '600',
                                        backgroundColor: comm.status === 'paid' ? 'rgba(16,185,129,0.15)' : comm.status === 'pending' ? 'rgba(251,191,36,0.15)' : comm.status === 'voided' ? 'rgba(239,68,68,0.15)' : 'rgba(33,138,255,0.15)',
                                        color: comm.status === 'paid' ? '#10b981' : comm.status === 'pending' ? '#fbbf24' : comm.status === 'voided' ? '#ef4444' : '#218aff',
                                      }}>
                                        {comm.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p style={{ color: '#666', fontSize: '13px', textAlign: 'center' }}>No commissions yet for this code.</p>
                        )}
                      </>
                    ) : (
                      <p style={{ color: '#666', fontSize: '13px' }}>Failed to load detail.</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Rename default code (one-time) */}
        {defaultCode && !defaultCode.is_custom && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            backgroundColor: 'rgba(33, 138, 255, 0.05)',
            border: '1px solid rgba(33, 138, 255, 0.2)',
            borderRadius: '8px',
          }}>
            <p style={{ color: '#ccc', fontSize: '13px', marginBottom: '8px' }}>
              Your default code is auto-generated. Customize it once: <strong style={{ color: '#fbbf24' }}>this cannot be undone.</strong>
            </p>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <input
                type="text"
                value={renameCode}
                onChange={(e) => setRenameCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="e.g., optionsguru"
                maxLength={30}
                style={inputStyle}
              />
              <button
                onClick={handleRenameDefault}
                disabled={renaming || !renameCode.trim()}
                style={{ ...btnStyle, backgroundColor: renaming || !renameCode.trim() ? '#333' : '#218aff' }}
              >
                {renaming ? 'Setting...' : 'Set Permanently'}
              </button>
            </div>
            {renameMsg && <p style={{ marginTop: '6px', fontSize: '13px', color: renameMsg.includes('Failed') ? '#ef4444' : '#10b981' }}>{renameMsg}</p>}
          </div>
        )}
      </section>

      {/* Payout Details */}
      <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-white mb-4">Payout Details</h2>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
          <select value={payoutMethod} onChange={(e) => setPayoutMethod(e.target.value)} style={{ ...inputStyle, minWidth: '140px' }}>
            <option value="">Select method...</option>
            <option value="paypal">PayPal</option>
            <option value="venmo">Venmo</option>
            <option value="zelle">Zelle</option>
            <option value="bank">Bank Transfer</option>
          </select>
          <input
            type="text"
            value={payoutHandle}
            onChange={(e) => setPayoutHandle(e.target.value)}
            placeholder="Email, handle, or phone..."
            style={{ ...inputStyle, flex: 1 }}
          />
        </div>
        {payoutMethod === 'bank' && (
          <textarea
            value={payoutNotes}
            onChange={(e) => setPayoutNotes(e.target.value)}
            placeholder="Bank routing & account number..."
            rows={2}
            style={{ ...inputStyle, width: '100%', resize: 'vertical', marginBottom: '10px' }}
          />
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <button onClick={handleSavePayout} disabled={savingPayout} style={{ ...btnStyle, backgroundColor: savingPayout ? '#333' : '#218aff' }}>
            {savingPayout ? 'Saving...' : 'Save Payout Details'}
          </button>
          {payoutMessage && <span style={{ fontSize: '13px', color: payoutMessage === 'Saved.' ? '#10b981' : '#ef4444' }}>{payoutMessage}</span>}
        </div>
      </section>

      {/* Recent Commissions (all codes) */}
      {dashboard.recent_commissions && dashboard.recent_commissions.length > 0 && (
        <section className="bg-surface rounded-lg border border-muted p-6 mb-6">
          <h2 className="text-lg font-semibold text-brand-white mb-4">Recent Commissions</h2>
          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #333', borderRadius: '8px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#111' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Campaign</th>
                  <th style={thStyle}>User</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Payment</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Rate</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Commission</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.recent_commissions.map((c) => (
                  <tr key={c.id} style={{ borderBottom: '1px solid #222' }}>
                    <td style={tdStyle}>{new Date(c.created_at).toLocaleDateString()}</td>
                    <td style={{ ...tdStyle, color: '#999' }}>{c.campaign_label || '-'}</td>
                    <td style={tdStyle}>{c.referred_username}</td>
                    <td style={{ ...tdStyle, textAlign: 'right' }}>{formatCents(c.payment_amount_cents)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center', color: '#999' }}>{(c.commission_rate * 100).toFixed(0)}%</td>
                    <td style={{ ...tdStyle, textAlign: 'right', color: '#10b981', fontWeight: '600' }}>{formatCents(c.commission_amount_cents)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600',
                        backgroundColor: c.status === 'paid' ? 'rgba(16,185,129,0.15)' : c.status === 'pending' ? 'rgba(251,191,36,0.15)' : c.status === 'voided' ? 'rgba(239,68,68,0.15)' : 'rgba(33,138,255,0.15)',
                        color: c.status === 'paid' ? '#10b981' : c.status === 'pending' ? '#fbbf24' : c.status === 'voided' ? '#ef4444' : '#218aff',
                      }}>
                        {c.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

const inputStyle = {
  padding: '8px 12px',
  backgroundColor: '#111',
  border: '1px solid #333',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '14px',
};

const btnStyle = {
  padding: '8px 16px',
  backgroundColor: 'transparent',
  border: '1px solid #333',
  borderRadius: '6px',
  color: '#fff',
  fontSize: '13px',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const thStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  color: '#999',
  borderBottom: '1px solid #333',
  fontWeight: '600',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '8px 12px',
  color: '#ccc',
  whiteSpace: 'nowrap',
};

export default AffiliatePage;
